// src/contexts/ToastContext.jsx
import React, { createContext, useContext, useState } from 'react';
import { nanoid } from 'nanoid';
import Toast from '../components/Toast';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = React.useCallback((message, type = 'info', duration = 5000) => {
        const id = nanoid();
        const newToast = { id, message, type, duration };
        setToasts(prev => [...prev, newToast]);
    }, []);

    const removeToast = React.useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const showSuccess = React.useCallback((message) => showToast(message, 'success'), [showToast]);
    const showError = React.useCallback((message) => showToast(message, 'error'), [showToast]);
    const showWarning = React.useCallback((message) => showToast(message, 'warning'), [showToast]);
    const showInfo = React.useCallback((message) => showToast(message, 'info'), [showToast]);

    const value = React.useMemo(() => ({
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo
    }), [showToast, showSuccess, showError, showWarning, showInfo]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            {/* Renderizar toasts */}
            <div className="fixed top-4 right-4 z-50 space-y-2">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        duration={toast.duration}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
}; 